import * as React from 'react';
import {
  TextInput,
  SelectInput,
  ImageInput,
  ImageField,
  required,
  ReferenceInput,
  useTranslate,
  useLocaleState,
} from 'react-admin';
import { RichTextInput } from 'ra-input-rich-text';
import { Box, Typography } from '@mui/material';

import InputRow from '../form/InputRow';
import FormSection from '../form/FormSection';
import SelectUserByEmailInput from '../form/SelectUserByEmailInput';
import SourceLanguageBanner from '../form/SourceLanguageBanner';
import { useSourceLanguage } from '../../hooks/useSourceLanguage';

const FieldHint = ({ children }) => (
  <Typography
    component="span"
    sx={{ display: 'inline-block', fontSize: 12, mb: 1, color: 'error.main' }}
  >
    {children}
  </Typography>
);

/**
 * Organizatör create/edit — web OrganisationCompanyApplicationForm ile aynı yapı.
 */
export function OrganisationCompanyFormFields({
  isEdit = false,
  record = null,
  permissions = null,
}) {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      <FormSection
        title={translate('organisationCompanies.company_details', {
          _: 'Şirket Bilgileri',
        })}
      >
        <InputRow>
          <TextInput
            source="name"
            label={translate('organisationCompanies.name_short', { _: 'Ad' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="slug"
            label="Slug"
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </InputRow>
        <InputRow>
          <TextInput
            source="phone"
            label={translate('venues.phone_short', { _: 'Tel' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="email"
            label="E-posta"
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </InputRow>
      </FormSection>

      <FormSection
        title={translate('organisationCompanies.about_company', {
          _: 'Şirket Hakkında',
        })}
      >
        <RichTextInput
          source={localizedSource('about')}
          multiline
          rows={5}
          label={translate('venues.about_short', { _: 'Hakkında' })}
          fullWidth
          validate={required()}
        />
      </FormSection>

      <FormSection title={translate('venues.media', { _: 'Medya' })}>
        <InputRow>
          <Box>
            <FieldHint>
              {translate('venues.square_img_hint', {
                _: 'Kare oranlı görsel yüklenmelidir.',
              })}
            </FieldHint>
            <ImageInput source="logo" label="Logo" accept="image/*" defaultValue={null}>
              <ImageField source="src" />
            </ImageInput>
          </Box>
          <Box>
            <FieldHint>
              {translate('venues.landscape_img_hint', {
                _: 'Yatay oranlı görsel yüklenmelidir.',
              })}
            </FieldHint>
            <ImageInput source="banner" label="Banner" accept="image/*" defaultValue={null}>
              <ImageField source="src" />
            </ImageInput>
          </Box>
        </InputRow>
      </FormSection>

      <FormSection title={translate('venues.address_info', { _: 'Adres' })}>
        <InputRow>
          <ReferenceInput source="city" reference="cities">
            <SelectInput
              optionText={localeNameKey}
              label={translate('venues.city', { _: 'Şehir' })}
              fullWidth
              variant="outlined"
              validate={required()}
            />
          </ReferenceInput>
          <TextInput
            source="address"
            label={translate('venues.address_short', { _: 'Adres' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
        </InputRow>
      </FormSection>

      <FormSection title={translate('venues.legal', { _: 'Yasal' })}>
        <InputRow columns={3}>
          <SelectInput
            source="company.type"
            label={translate('venues.type_short', { _: 'Tip' })}
            choices={[
              { id: 'K', name: 'KK' },
              { id: 'M', name: 'MŞ' },
              { id: 'Y', name: 'YŞ' },
              { id: 'R', name: 'RK' },
            ]}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="company.name"
            label={translate('venues.entity_short', { _: 'Tüzel' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
          <TextInput
            source="company.taxName"
            label={translate('venues.tax_name_short', { _: 'Vergi adı' })}
            fullWidth
            variant="outlined"
            validate={required()}
          />
        </InputRow>
        <ImageInput
          source="taxDocument"
          label={translate('venues.tax_doc_short', { _: 'Vergi belgesi' })}
          accept="image/*"
          defaultValue={null}
        >
          <ImageField source="src" />
        </ImageInput>
      </FormSection>

      <FormSection title={translate('venues.financial', { _: 'Finansal' })}>
        <InputRow columns={3}>
          <TextInput
            source="bank.companyName"
            label={translate('venues.account_short', { _: 'Hesap adı' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
          <TextInput
            source="bank.name"
            label={translate('venues.bank_short', { _: 'Banka' })}
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
          <TextInput
            source="bank.accountNo"
            label="IBAN"
            fullWidth
            variant="outlined"
            defaultValue={null}
          />
        </InputRow>
      </FormSection>

      <FormSection
        title={translate('organisationCompanies.managers', {
          _: 'Yöneticiler',
        })}
      >
        <SelectUserByEmailInput
          source="managers"
          multiple
          required={false}
          record={isEdit ? record : undefined}
        />
      </FormSection>
    </>
  );
}

export default OrganisationCompanyFormFields;
